import { cn } from '../cn.ts';

export type ButtonVariant =
  'primary' | 'secondary' | 'ghost' | 'inverse' | 'on-dark' | 'on-dark-cta';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * `font-weight` KHÔNG nằm ở đây mà ở từng cỡ: thiết kế cho nút lớn weight 700 và nút thường 600
 * (`reference.md` §3 "Nút"). Để `font-semibold` ở BASE rồi thêm `font-bold` ở cỡ `lg` là đặt hai
 * utility cùng thuộc tính cạnh nhau — `cn()` chỉ nối chuỗi nên thắng thua do thứ tự Tailwind xếp
 * trong CSS, không do thứ tự ta viết.
 */
const BASE =
  'inline-flex items-center justify-center gap-2 rounded-pill transition ' +
  '[transition-duration:var(--duration-hover)] focus-visible:outline-2 ' +
  'focus-visible:outline-offset-2 ' +
  'focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-50 ' +
  // Lún 1px khi bấm. Trên mobile đây là tín hiệu DUY NHẤT rằng cú chạm đã nhận — không có
  // hover để thay thế. Đặt ở BASE, KHÔNG nhồi qua className: `cn()` chỉ nối chuỗi nên hai
  // utility cùng thuộc tính sẽ tranh nhau theo thứ tự Tailwind trong CSS, không theo thứ tự ta
  // viết. Không cần khai thêm thuộc tính transition: `transition` trần đã bao `transform`.
  'active:translate-y-px';

/**
 * Mọi trạng thái hover/active dưới đây lấy từ `style-hover` có sẵn trong bản thiết kế
 * (reference.md §3 "Nút") — không sinh màu mới.
 *
 * `on-dark` là nút secondary trên nền tối: viền trắng-alpha, hover đổi NỀN chứ không đổi viền.
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white shadow-btn-brand hover:bg-brand-900 active:bg-brand-950',
  secondary: 'bg-ink-100 text-ink-900 hover:bg-ink-200 active:bg-ink-300',
  ghost: 'text-ink-700 hover:bg-ink-50 active:bg-ink-100',
  inverse: 'bg-white text-brand-600 hover:bg-ink-50 active:bg-ink-100',
  // `active` đổi NỀN đậm thêm một bậc so với hover — 18% là mức có thật trong `theme.css`, nằm
  // giữa `--on-dark-fill-16` và `--on-dark-fill-26`. Viền giữ nguyên, đúng như hover.
  //
  // `on-dark` phục vụ nút phụ của HERO: thiết kế cho nó viền 42% và hover nền 12%.
  'on-dark':
    'border border-[var(--on-dark-line-42)] text-white hover:bg-[var(--on-dark-fill-12)] ' +
    'active:bg-[var(--on-dark-fill-18)]',
  /*
   * Nút phụ của `#cta` — biến thể RIÊNG, không phải bậc thứ hai của `on-dark`.
   *
   * Vì sao phải là biến thể chứ không truyền thêm class ở chỗ gọi: hai utility cùng thuộc tính
   * `border-color` sẽ CÙNG SỐNG vì `cn()` chỉ nối chuỗi, không phải tailwind-merge — thắng thua
   * do thứ tự Tailwind xếp trong CSS, không do thứ tự ta viết. Đó là quyết định D-3 của user
   * (vé #10); đường "nhồi qua className" đã bị bác kèm lý do.
   *
   * Token 45% KHÔNG mới: nó có sẵn và chú thích của nó gọi đích danh nút này. Trước vé #10 nó là
   * token duy nhất trong thang viền trắng-alpha KHÔNG có hộ tiêu thụ nào.
   *
   * Hai nút lệch nhau ở HAI thuộc tính, không phải một — và bảng nợ của lô D chỉ khai một. Đo tay
   * trên bản thiết kế: hero dòng 617 viền 42% + hover nền 12%; `#cta` dòng 1048 viền 45% + hover
   * nền 14%. Nợ viền trả ở vé #10 (D-3), nợ hover trả ở vé #38 — hai vé vì user quyết riêng từng
   * giá trị: alpha là GIÁ TRỊ, và luật 5 của skill giao giá trị cho user.
   *
   * ⚠️ Bài học của cặp này, giữ lại vì nó là lý do vé #38 tồn tại: **một bảng nợ là TÀI LIỆU,
   * không phải CỔNG.** Bảng nợ lô D khai thiếu một nửa của D-3, và không cổng nào canh chính bảng
   * đó. Ai trả một nợ theo bảng thì đo lại chỗ thiết kế, đừng chỉ đọc dòng của bảng.
   */
  'on-dark-cta':
    'border border-[var(--on-dark-line-45)] text-white hover:bg-[var(--on-dark-fill-14)] ' +
    'active:bg-[var(--on-dark-fill-18)]',
};

/**
 * Cao theo thiết kế ở `md` lên; DƯỚI `md` mọi nút cao tối thiểu 44px (skill luật 6 — WCAG 2.5.5).
 * Đây là chỗ duy nhất được phép có breakpoint cho kích thước nút, và nó không phải thang chữ.
 */
const SIZE: Record<ButtonSize, string> = {
  sm: 'min-h-11 px-3 text-caption md:min-h-[34px] md:px-3',
  md: 'min-h-11 px-5 text-body md:min-h-10',
  // 15.5px — đo ở `template.html:613`. Weight KHÔNG nằm ở đây: nút chính là 700 còn nút viền
  // trên nền tối là 600 (dòng 614), tức weight đi theo BIẾN THỂ chứ không theo cỡ.
  lg: 'min-h-11 px-6 text-title-sm md:min-h-13 md:px-7',
};

/**
 * Weight phụ thuộc CẢ cỡ LẪN biến thể — đo từ thiết kế, không suy từ một quy tắc gọn:
 *   CTA trong header, cỡ `md`, nền đặc   → 600 (`template.html:601`)
 *   nút chính của hero, cỡ `lg`, nền đặc → 700 (`:613`)
 *   nút phụ của hero, cỡ `lg`, viền      → 600 (`:614`)
 * Tức chỉ nút ĐẶC và CỠ LỚN mới 700. Rút gọn thành "700 theo biến thể" làm CTA header dày lên;
 * rút gọn thành "700 theo cỡ" làm nút phụ của hero dày lên. Cả hai bản rút gọn đó đều đã bị cổng
 * `design-diff` bắt, mỗi bản một lượt.
 */
function weightFor(variant: ButtonVariant, size: ButtonSize): string {
  const solid = variant === 'primary' || variant === 'inverse';
  return solid && size === 'lg' ? 'font-bold' : 'font-semibold';
}

/**
 * Class của button tách thành hàm riêng, ở file riêng, để dùng lại cho THẺ KHÁC — cụ thể là
 * `<Link>` của react-router.
 *
 * Không có nó thì người ta sẽ bọc `<Button>` trong `<Link>`, sinh ra `<a><button>`: HTML sai
 * cấu trúc và screen reader đọc thành hai điểm tương tác lồng nhau.
 *
 * File riêng (không nằm trong `Button.tsx`) vì một module vừa xuất component vừa xuất hàm
 * thường sẽ làm hot-reload phải nạp lại cả cây thay vì chỉ component đang sửa.
 */
export function buttonClasses(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  className?: string,
): string {
  return cn(BASE, VARIANT[variant], weightFor(variant, size), SIZE[size], className);
}
